// BodyMap — thin, theme-injected wrapper around react-native-body-highlighter.
//
// Lifted from apps/mobile into @heraion/ui (2026-07-01) so web-rnw renders
// the real figure instead of a "view on mobile" placeholder. The only thing
// that was mobile-local was the theme source: this version takes `theme`
// (structurally a UiTheme — the mobile useTheme() object is assignable) and an
// `isDark` flag as props. The mobile side keeps a 5-line shim that injects
// useTheme(); web passes resolveTheme(dark, family) + the same dark flag, so
// both platforms share one figure and the grey→peak heatmapRamp renders on web.
//
// Canonical 20-slug API from @heraion/shared. rnbh uses a single "deltoids"
// slug for both front and back; this wrapper translates our "front-deltoids" /
// "back-deltoids" into "deltoids" + the visible view, so the picker paints the
// front cap without confusing the lib's data model (and vice versa on the back).

import { useMemo } from "react";
import { View, StyleSheet, Pressable, Text } from "react-native";
import { useTranslation } from "react-i18next";
import * as RNBHModule from "react-native-body-highlighter";
import type {
  ExtendedBodyPart,
  Slug as RNBHSlug,
} from "react-native-body-highlighter";

// react-native-body-highlighter ships a CJS default export. Metro (mobile)
// resolves `import Body from` to the component directly. Vite/rolldown, however,
// wraps the CJS require with esbuild's __toESM(mod, /*isNodeMode*/ 1) — whose
// node-mode branch defines `.default` as the WHOLE module object instead of
// honouring the module's own `exports.default`. A plain default import is then
// the `{ default, __esModule }` object on web, and rendering it crashes with
// "element type is invalid" (React #130). Unwrap the interop defensively: this
// resolves to the Body component on both platforms (on Metro the first `.default`
// is already the component, so the nested `.default` is skipped).
const RNBHDefault = (RNBHModule as { default?: unknown }).default;
const Body = (((RNBHDefault as { default?: unknown } | undefined)?.default ??
  RNBHDefault ??
  RNBHModule) as typeof import("react-native-body-highlighter").default);

import {
  BODY_SIDES,
  MUSCLE_VIEW,
  type BodyFigure,
  type BodyMapValue,
  type BodySide,
  type BodyView,
  type MusclePaint,
  type MuscleSlug,
} from "@heraion/shared";

import type { UiTheme } from "../theme";
import { DRAWN_MUSCLES } from "./drawnMuscles";

export type BodyMapMode = "pick" | "display";

export type BodyMapProps = {
  mode: BodyMapMode;
  view: BodyView;
  value: BodyMapValue;
  // Theme is host-provided (mobile useTheme() / web resolveTheme). isDark picks
  // the figure border (borderStrong on dark, border on light) — UiTheme itself
  // doesn't carry the scheme name, so the flag comes alongside.
  theme: UiTheme;
  isDark?: boolean;
  // `side` is the athlete's anatomical side of the tapped half (PG-14);
  // absent when the lib drew the part without halves (head, hair).
  onMusclePress?: (slug: MuscleSlug, side?: BodySide) => void;
  onViewToggle?: () => void;
  scale?: number;
  /**
   * Fill a SQUARE box of this side, in points, instead of drawing at a fixed
   * `scale`. Wins over `scale` when given.
   *
   * ⛔ Not a guess and not a tuned constant: the library draws each figure in
   * a 200×400 unit box multiplied by `scale`
   * (`react-native-body-highlighter/dist/components/SvgMaleWrapper.js:41`),
   * so the dual pair is 400×400 units — exactly square. Filling a square box
   * is therefore a division, and the same division is right on every screen
   * density and every device.
   *
   * ⭐ Why it exists (Antenore, 2026-09-02, on the first APK of LG-59): «è più
   * piccolo dello spazio disponibile, si vede dallo screenshot». The figures
   * had always been drawn at a fixed 1.2×0.55 = 0.66 and never looked at the
   * box they were in; the history page only made the unused room visible.
   */
  fitTo?: number;
  /** Same box as `fitTo`, but it only ever shrinks the `scale` size: a
   *  fixed-size figure that must not overflow a narrow column. */
  shrinkTo?: number;
  // Mapped to rnbh's gender prop ('feminine' → 'female'; anything else,
  // including absent, → 'male'). Consumer surfaces read profile.preferredFigure
  // and pass it through.
  figure?: BodyFigure;
  // 'single' (default) renders one figure and exposes the front/back toggle
  // when onViewToggle is provided. 'dual' renders front + back side-by-side and
  // drops the toggle (it would be a no-op when both views are visible).
  // The pain picker is dual too: its muscles sit under 44pt at 1x, and its
  // page's ZoomPanView is what brings them up to a tappable size.
  layout?: "single" | "dual";
  /** Paint a muscle on every figure that draws it (the pain map: a tap on
   *  front triceps must show there). Default: only on its MUSCLE_VIEW. */
  everyView?: boolean;
};

// ⛔ PG-14 (2026-09-24): rnbh's `left` / `right` are SCREEN halves, not the
// athlete's sides. Measured on all four 3.2.0 assets (male and female, front
// and back): every `path.left` sits at a smaller x than its `path.right`. On
// the FRONT view the figure faces you, so screen-left is the athlete's RIGHT;
// on the BACK view screen-left is the athlete's LEFT. The swap is its own
// inverse, so this one function translates both ways: painting (anatomical →
// screen) and tapping (screen → anatomical).
export function rnbhSideFor(side: BodySide, view: BodyView): BodySide {
  if (view === "back") return side;
  return side === "left" ? "right" : "left";
}

// Slugs that pass straight through to rnbh: all but the two deltoid caps.
const RNBH_DIRECT: ReadonlySet<string> = new Set<string>(
  DRAWN_MUSCLES.filter((s) => s !== "front-deltoids" && s !== "back-deltoids"),
);

// One rnbh entry per painted half. Absent side = both halves. Two colours =
// one entry per side: the patched rnbh keys its data by slug and side (#307).
const rnbhEntries = (
  slug: RNBHSlug,
  paint: MusclePaint,
  view: BodyView,
): ExtendedBodyPart[] => {
  const base = { slug, intensity: paint.intensity };
  const { sideColours } = paint;
  if (sideColours) {
    return BODY_SIDES.map((side) => ({
      ...base,
      color: sideColours[side],
      side: rnbhSideFor(side, view),
    }));
  }
  return [
    {
      ...base,
      color: paint.colour,
      ...(paint.side ? { side: rnbhSideFor(paint.side, view) } : {}),
    },
  ];
};

function toRNBHData(
  value: BodyMapValue,
  view: BodyView,
  everyView: boolean,
): ExtendedBodyPart[] {
  const out: ExtendedBodyPart[] = [];
  for (const [rawSlug, paint] of Object.entries(value)) {
    if (!paint) continue;
    const slug = rawSlug as MuscleSlug;
    if (slug === "front-deltoids") {
      if (view === "front") out.push(...rnbhEntries("deltoids", paint, view));
      continue;
    }
    if (slug === "back-deltoids") {
      if (view === "back") out.push(...rnbhEntries("deltoids", paint, view));
      continue;
    }
    if (slug === "abductors") {
      // rnbh has no abductors slug; the closest visual cue is the gluteal area
      // on the back view. Silently drop; picker UX relies on the
      // back-deltoids / gluteal taps for hip pain.
      continue;
    }
    if (RNBH_DIRECT.has(slug)) {
      // A muscle stays on its own view, unless `everyView`: rnbh draws only
      // the slugs its asset has, so no part leaks onto a figure without it.
      const sidedness = MUSCLE_VIEW[slug];
      if (!everyView && sidedness !== "both" && sidedness !== view) continue;
      out.push(...rnbhEntries(slug as RNBHSlug, paint, view));
    }
  }
  return out;
}

// Map rnbh's onBodyPartPress result back to our canonical slug. The only
// ambiguous case is "deltoids" — the current view decides front vs back cap.
function fromRNBHSlug(rnbhSlug: RNBHSlug, view: BodyView): MuscleSlug | null {
  if (rnbhSlug === "deltoids") {
    return view === "front" ? "front-deltoids" : "back-deltoids";
  }
  return RNBH_DIRECT.has(rnbhSlug) ? (rnbhSlug as MuscleSlug) : null;
}

// The two insets `fitTo` has to subtract, named once and read by both the
// arithmetic in the component and the stylesheet at the bottom, so they
// cannot drift.
const WRAP_PADDING = 8;
const DUAL_GAP = 8;

export function BodyMap({
  mode,
  view,
  value,
  theme,
  isDark = false,
  onMusclePress,
  onViewToggle,
  scale = 1.4,
  fitTo,
  shrinkTo,
  figure,
  layout = "single",
  everyView = false,
}: BodyMapProps) {
  const { t } = useTranslation();
  // rnbh's gender prop uses sex terms ('male' / 'female') while our schema uses
  // figure terms ('masculine' / 'feminine') decoupled from sex. Map at the
  // boundary; absent figure resolves to 'male' (the historical default).
  const gender = figure === "feminine" ? "female" : "male";

  // Dual halves the per-figure scale (x0.55) so the pair fits a single figure's slot.
  const isDual = layout === "dual";
  // ⛔ `wrap` adds 8 of vertical padding and the dual row 8 of gap between the
  // two figures, so the drawable square is smaller than the box by both. Take
  // the tighter of the two constraints rather than assuming which one binds:
  // the pair is square in UNITS, but its padding is not symmetric.
  const scaleFor = (box: number) =>
    isDual
      ? Math.min((box - DUAL_GAP) / 400, (box - WRAP_PADDING * 2) / 400)
      : Math.min((box - WRAP_PADDING * 2) / 400, box / 200);
  const natural = isDual ? scale * 0.55 : scale;
  const perFigureScale = Math.max(
    fitTo !== undefined
      ? scaleFor(fitTo)
      : shrinkTo !== undefined
        ? Math.min(natural, scaleFor(shrinkTo))
        : natural,
    0,
  );
  const dataFront = useMemo(() => toRNBHData(value, "front", everyView), [value, everyView]);
  const dataBack = useMemo(() => toRNBHData(value, "back", everyView), [value, everyView]);
  const dataSingle = useMemo(
    () => toRNBHData(value, view, everyView),
    [value, view, everyView],
  );

  const renderFigure = (side: BodyView, bodyData: ExtendedBodyPart[]) => (
    <Body
      data={bodyData}
      side={side}
      scale={perFigureScale}
      gender={gender}
      defaultFill={theme.palette.surface2}
      defaultStroke={theme.palette.borderStrong}
      defaultStrokeWidth={1}
      border={isDark ? theme.palette.borderStrong : theme.palette.border}
      onBodyPartPress={
        mode === "pick" && onMusclePress
          ? (bp, screenSide) => {
              if (!bp.slug) return;
              const slug = fromRNBHSlug(bp.slug, side);
              if (!slug) return;
              onMusclePress(
                slug,
                screenSide ? rnbhSideFor(screenSide, side) : undefined,
              );
            }
          : undefined
      }
    />
  );

  return (
    <View style={styles.wrap}>
      {/* rnbh gives every path an onPress: a display figure ate its host's taps. */}
      <View
        style={isDual ? styles.bodyWrapDual : styles.bodyWrap}
        pointerEvents={mode === "display" ? "none" : "auto"}
      >
        {isDual ? (
          <>
            <View style={styles.bodyWrap}>
              {renderFigure("front", dataFront)}
              {mode === "pick" ? (
                <Text
                  style={[
                    styles.caption,
                    { color: theme.palette.ink2, fontSize: theme.typography.sizes.sm },
                  ]}
                >
                  {t("bodymap.viewFront")}
                </Text>
              ) : null}
            </View>
            <View style={styles.bodyWrap}>
              {renderFigure("back", dataBack)}
              {mode === "pick" ? (
                <Text
                  style={[
                    styles.caption,
                    { color: theme.palette.ink2, fontSize: theme.typography.sizes.sm },
                  ]}
                >
                  {t("bodymap.viewBack")}
                </Text>
              ) : null}
            </View>
          </>
        ) : (
          renderFigure(view, dataSingle)
        )}
      </View>
      {/* No front/back toggle in dual-view: both are visible. */}
      {!isDual && onViewToggle ? (
        <Pressable
          accessibilityRole="button"
          onPress={onViewToggle}
          style={({ pressed }) => [
            styles.viewToggle,
            {
              backgroundColor: theme.palette.surface,
              borderColor: theme.palette.border,
              opacity: pressed ? 0.7 : 1,
            },
          ]}
        >
          <Text style={[styles.viewToggleLabel, { color: theme.palette.ink }]}>
            {view === "front"
              ? `${t("bodymap.viewBack")} ›`
              : `‹ ${t("bodymap.viewFront")}`}
          </Text>
        </Pressable>
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    alignItems: "center",
    paddingVertical: WRAP_PADDING,
  },
  bodyWrap: {
    alignItems: "center",
    justifyContent: "center",
  },
  // Front/back caption under each figure in the dual pick-mode picker
  // (colour + size come from the theme, applied inline where the figure
  // is rendered: this stylesheet has no theme access).
  caption: {
    marginTop: 4,
    fontWeight: "600",
  },
  bodyWrapDual: {
    flexDirection: "row",
    alignItems: "flex-start",
    justifyContent: "center",
    gap: DUAL_GAP,
  },
  viewToggle: {
    marginTop: 12,
    paddingVertical: 8,
    paddingHorizontal: 16,
    minHeight: 44,
    minWidth: 110,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderRadius: 999,
  },
  viewToggleLabel: {
    fontSize: 14,
    fontWeight: "600",
    letterSpacing: 0.2,
  },
});
